import React from "react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { buildWhatsAppUrl } from "@/config/contact";
import { BadgeCheck, CalendarClock, FileText, ShieldCheck } from "lucide-react";

interface LegalModalProps {
  title: string;
  content: string;
  triggerText: string;
  className?: string;
}

type LegalSection = {
  heading: string;
  blocks: Array<
    | { type: "paragraph"; content: string }
    | { type: "list"; items: string[]; ordered: boolean }
  >;
};

type LegalTheme = {
  icon: React.ComponentType<{ className?: string; "aria-hidden"?: boolean }>;
  eyebrow: string;
  highlight: string;
  badgeClass: string;
  sectionBorderClass: string;
  sectionNumberClass: string;
  bulletDotClass: string;
  secondaryButtonHoverClass: string;
};

const headingKeywords = [
  "objetivo",
  "dados pessoais",
  "finalidades",
  "compartilhamento",
  "seguranca",
  "segurança",
  "término",
  "termino",
  "direitos",
  "revogacao",
  "revogação",
  "atendimento",
  "contato",
  "privacidade",
  "pagamentos",
  "cancelamentos",
  "cadastro",
  "responsabilidades",
  "propriedade intelectual",
  "modificacoes",
  "modificações",
  "sobre a plataforma",
  "relacao com organizadores",
  "relação com organizadores",
  "regras gerais",
  "cancelamento pelo participante",
  "cancelamento pelo organizador",
  "casos de forca maior",
  "casos de força maior",
  "taxas e custos",
  "como solicitar ajuda",
];

const isHeadingLine = (line: string) => {
  const normalized = line.trim();
  const lower = normalized.replace(/^\d+\.\s*/, "").toLowerCase();

  return (
    headingKeywords.some(
      (keyword) => lower === keyword || lower.startsWith(`${keyword} `)
    ) ||
    (/^[A-ZÀ-Ú][A-Za-zÀ-ÿ\s-]+$/.test(normalized) &&
      normalized.length <= 80 &&
      !normalized.endsWith("."))
  );
};

const normalizeHeading = (heading: string) => heading.replace(/^\d+\.\s*/, "").trim();

const parseLegalSections = (content: string): LegalSection[] => {
  const sections: LegalSection[] = [];
  let currentSection: LegalSection = {
    heading: "Visão geral",
    blocks: [],
  };
  let paragraphLines: string[] = [];
  let listItems: string[] = [];
  let listIsOrdered = false;

  const pushCurrentSection = () => {
    if (currentSection.blocks.length) {
      sections.push(currentSection);
    }
  };

  const flushParagraph = () => {
    if (!paragraphLines.length) return;
    currentSection.blocks.push({
      type: "paragraph",
      content: paragraphLines.join(" "),
    });
    paragraphLines = [];
  };

  const flushList = () => {
    if (!listItems.length) return;
    currentSection.blocks.push({
      type: "list",
      items: listItems,
      ordered: listIsOrdered,
    });
    listItems = [];
  };

  content.split("\n").forEach((rawLine) => {
    const line = rawLine.trim();

    if (!line) {
      flushParagraph();
      flushList();
      return;
    }

    if (isHeadingLine(line)) {
      flushParagraph();
      flushList();
      pushCurrentSection();
      currentSection = {
        heading: normalizeHeading(line),
        blocks: [],
      };
      return;
    }

    const unorderedMatch = line.match(/^[-•]\s+(.+)/);
    const orderedMatch = line.match(/^(?:\d+\.|[IVXLC]+\s+[–-])\s*(.+)/i);
    if (unorderedMatch || orderedMatch) {
      flushParagraph();
      const ordered = Boolean(orderedMatch);
      if (listItems.length && listIsOrdered !== ordered) flushList();
      listIsOrdered = ordered;
      listItems.push((unorderedMatch?.[1] || orderedMatch?.[1] || line).trim());
      return;
    }

    flushList();
    paragraphLines.push(line);
  });

  flushParagraph();
  flushList();
  pushCurrentSection();

  return sections.length
    ? sections
    : [
        {
          heading: "Visão geral",
          blocks: [{ type: "paragraph", content }],
        },
      ];
};

const renderInlineMarkdown = (value: string) =>
  value.split(/(\*\*.+?\*\*)/g).map((part, index) =>
    part.startsWith("**") && part.endsWith("**") ? (
      <strong key={`${part}-${index}`} className="font-semibold text-[#173324]">
        {part.slice(2, -2)}
      </strong>
    ) : (
      <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>
    )
  );

const getModalTheme = (title: string): LegalTheme => {
  const normalizedTitle = title.toLowerCase();

  if (normalizedTitle.includes("consentimento")) {
    return {
      icon: BadgeCheck,
      eyebrow: "Consentimento e LGPD",
      highlight: "Transparência sobre coleta, tratamento, retenção e direitos do titular.",
      badgeClass: "bg-[#EAF0F4] text-[#1F4E6B]",
      sectionBorderClass: "border-[#D6DFE6]",
      sectionNumberClass: "bg-[#EAF0F4] text-[#1F4E6B]",
      bulletDotClass: "bg-[#1F4E6B]",
      secondaryButtonHoverClass: "hover:bg-[#EAF0F4]",
    };
  }

  if (normalizedTitle.includes("cancelamento")) {
    return {
      icon: CalendarClock,
      eyebrow: "Regras de cancelamento",
      highlight: "Prazos, elegibilidade para reembolso e critérios de remarcação com leitura objetiva.",
      badgeClass: "bg-[#EEF3F6] text-[#2F4858]",
      sectionBorderClass: "border-[#D7E1E6]",
      sectionNumberClass: "bg-[#EEF3F6] text-[#2F4858]",
      bulletDotClass: "bg-[#2F4858]",
      secondaryButtonHoverClass: "hover:bg-[#EEF3F6]",
    };
  }

  if (normalizedTitle.includes("privacidade")) {
    return {
      icon: ShieldCheck,
      eyebrow: "Privacidade e dados",
      highlight: "Como os dados são tratados, compartilhados e protegidos na plataforma.",
      badgeClass: "bg-[#ECF3EF] text-[#215834]",
      sectionBorderClass: "border-[#DCE7E0]",
      sectionNumberClass: "bg-[#ECF3EF] text-[#215834]",
      bulletDotClass: "bg-[#215834]",
      secondaryButtonHoverClass: "hover:bg-[#ECF3EF]",
    };
  }

  return {
    icon: FileText,
    eyebrow: "Documento legal",
    highlight: "Condições de uso, responsabilidades e orientações essenciais para utilização da plataforma.",
    badgeClass: "bg-[#ECF3EF] text-[#215834]",
    sectionBorderClass: "border-[#DCE7E0]",
    sectionNumberClass: "bg-[#ECF3EF] text-[#215834]",
    bulletDotClass: "bg-[#215834]",
    secondaryButtonHoverClass: "hover:bg-[#ECF3EF]",
  };
};

const LegalModal: React.FC<LegalModalProps> = ({ title, content, triggerText, className }) => {
  const sections = parseLegalSections(content);
  const theme = getModalTheme(title);
  const Icon = theme.icon;

  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          type="button"
          className={className || "text-left transition-colors hover:text-[#215834]"}
          aria-label={`Abrir ${triggerText}`}
        >
          {triggerText}
        </button>
      </DialogTrigger>

      <DialogContent className="max-h-[94vh] max-w-[min(96vw,72rem)] gap-0 overflow-hidden rounded-[28px] border border-[#D6DFE6] bg-white p-0 shadow-[0_28px_80px_rgba(22,40,30,0.24)] duration-300 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-100 data-[state=open]:slide-in-from-top-[48%] [&>button]:right-5 [&>button]:top-5 [&>button]:h-11 [&>button]:w-11 [&>button]:rounded-full [&>button]:bg-white [&>button]:text-[#173324] [&>button]:shadow-md [&>button]:ring-1 [&>button]:ring-[#173324]/10 [&>button_svg]:h-5 [&>button_svg]:w-5">
        <DialogHeader className="relative overflow-hidden border-b border-[#1A462B] bg-[#215834] px-6 py-6 text-left sm:px-8 sm:py-8">
          <div className="relative space-y-5 pr-12">
            <div className="flex items-start gap-4">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-white/12 text-white ring-1 ring-white/20 backdrop-blur-sm">
                <Icon className="h-7 w-7" aria-hidden="true" />
              </div>

              <div className="space-y-3">
                <span className="inline-flex rounded-full bg-white/12 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-white/90 ring-1 ring-white/15">
                  {theme.eyebrow}
                </span>
                <DialogTitle className="font-fraunces text-2xl font-bold leading-tight text-white sm:text-[2rem]">
                  {title}
                </DialogTitle>
                <DialogDescription className="max-w-3xl font-author text-sm leading-relaxed text-white/82 sm:text-base">
                  {theme.highlight}
                </DialogDescription>
              </div>
            </div>
          </div>
        </DialogHeader>

        <div className="grid min-h-0 lg:grid-cols-[minmax(0,1fr)_18rem]">
          <ScrollArea className="legal-scroll-area max-h-[calc(94vh-17rem)] px-6 py-6 sm:px-8 sm:py-8">
            <div className="space-y-5">
              {sections.map((section, index) => (
                <section
                  key={`${section.heading}-${index}`}
                  className={`rounded-[24px] border ${theme.sectionBorderClass} bg-white p-5 shadow-[0_10px_30px_rgba(23,51,36,0.06)] sm:p-6`}
                >
                  <div className="mb-4 flex items-start gap-3">
                    <span className={`inline-flex h-10 min-w-10 items-center justify-center rounded-full px-3 text-sm font-semibold ${theme.sectionNumberClass}`}>
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <div className="space-y-1">
                      <h3 className="font-fraunces text-xl font-semibold leading-tight text-[#173324]">
                        {section.heading}
                      </h3>
                    </div>
                  </div>

                  <div className="space-y-3 font-author text-sm leading-7 text-[#2C3E37] sm:text-[1rem]">
                    {section.blocks.map((block, blockIndex) =>
                      block.type === "paragraph" ? (
                        <p key={`${section.heading}-paragraph-${blockIndex}`}>
                          {renderInlineMarkdown(block.content)}
                        </p>
                      ) : (
                        <ul
                          key={`${section.heading}-list-${blockIndex}`}
                          className="space-y-3 rounded-2xl border border-[#E4ECE8] bg-[#F7FAF8] p-4"
                        >
                          {block.items.map((item, itemIndex) => (
                          <li
                            key={`${section.heading}-item-${blockIndex}-${itemIndex}`}
                            className="flex items-start gap-3"
                          >
                            <span className={`mt-2 inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold text-white ${theme.bulletDotClass}`}>
                              {block.ordered ? itemIndex + 1 : ""}
                            </span>
                            <span>{renderInlineMarkdown(item)}</span>
                          </li>
                          ))}
                        </ul>
                      )
                    )}
                  </div>
                </section>
              ))}
            </div>
          </ScrollArea>

          <aside className="hidden border-l border-[#D6DFE6] bg-[#F4F8F5] px-6 py-6 lg:flex lg:flex-col lg:justify-end">
            <div className="rounded-3xl border border-white bg-white p-4 shadow-sm">
              <p className="font-fraunces text-lg font-semibold text-[#173324]">Precisa de ajuda?</p>
              <p className="mt-2 font-author text-sm leading-6 text-[#52605C]">
                Nossa equipe pode orientar você sobre cadastro, reservas, políticas e dúvidas regulatórias da plataforma.
              </p>
              <a
                href={buildWhatsAppUrl("Olá! Estou revisando os documentos legais e preciso de ajuda.")}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex min-h-11 w-full items-center justify-center rounded-full bg-[#215834] px-4 py-3 text-sm font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5 hover:bg-[#184327] hover:shadow-lg"
              >
                Falar com suporte
              </a>
            </div>
          </aside>
        </div>

        <DialogFooter className="border-t border-[#D6DFE6] bg-white px-6 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-8">
          <p className="max-w-xl font-author text-sm leading-relaxed text-[#52605C]">
            Consulte este documento com rolagem interna, leitura confortável e suporte acessível sempre que precisar.
          </p>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <a
              href={buildWhatsAppUrl("Olá! Preciso de esclarecimentos sobre os documentos legais da plataforma.")}
              target="_blank"
              rel="noopener noreferrer"
              className={`inline-flex min-h-11 items-center justify-center rounded-full border border-[#D6DFE6] px-5 py-2.5 text-sm font-semibold text-[#173324] transition-colors ${theme.secondaryButtonHoverClass}`}
            >
              Tirar dúvidas
            </a>
            <DialogClose asChild>
              <button
                type="button"
                className="inline-flex min-h-11 items-center justify-center rounded-full bg-[#215834] px-5 py-2.5 text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#184327] hover:shadow-lg"
              >
                Entendi
              </button>
            </DialogClose>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LegalModal;
